import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  archiveCourse,
  CourseStatus,
  publishCourse,
  submitCourseForReview,
  unpublishCourse,
} from '../api/courses';
import { getMyCoursesWithEngagement, TeacherCourseSummary } from '../api/teacher-dashboard';
import ApplicationStatusBanner, {
  applicationApproved,
} from '../components/ApplicationStatusBanner';
import { useTeacherVerification } from '../hooks/useTeacherVerification';
import { useTeacherApplication } from '../hooks/useTeacherApplication';
import { describeSchedule } from '../domain/catalog';
import { formatMoney } from '../../../commodity/format/money';
import { PortalPage } from '../../../design-system/portal';

/** Short date for "last updated". Same locale as every other date on screen. */
const formatDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

/**
 * What the teacher sees a course as: its publication state, or — while it is
 * not live — where it stands with the reviewer (UC-A08). One chip rather than
 * two, because a teacher asks one question of it: "what is happening to my
 * course?"
 */
type DisplayState = CourseStatus | 'PENDING_REVIEW' | 'CHANGES_REQUIRED' | 'APPROVED' | 'REJECTED';

const displayStateOf = (course: TeacherCourseSummary): DisplayState => {
  if (course.status !== 'DRAFT') return course.status;
  const review = course.reviewStatus;
  return review && review !== 'DRAFT' ? review : 'DRAFT';
};

const STATUS_LABEL: Record<DisplayState, string> = {
  DRAFT: 'Borrador',
  PUBLISHED: 'Publicado',
  ARCHIVED: 'Archivado',
  PENDING_REVIEW: 'En revisión',
  CHANGES_REQUIRED: 'Cambios solicitados',
  // APPROVED ≠ PUBLISHED (AD-A08-05): said in as many words, so a teacher
  // whose approved course is not live is not left wondering why.
  APPROVED: 'Aprobado · sin publicar',
  REJECTED: 'Rechazado',
};

const STATUS_TONE: Record<DisplayState, { bg: string; fg: string }> = {
  DRAFT: { bg: '#eef1f5', fg: '#5a6672' },
  PUBLISHED: { bg: '#edf8e5', fg: '#3f7410' },
  ARCHIVED: { bg: '#f3e8ff', fg: '#6b30c9' },
  PENDING_REVIEW: { bg: '#eaf2fb', fg: '#1c4f80' },
  CHANGES_REQUIRED: { bg: '#fff8e6', fg: '#7a4d06' },
  APPROVED: { bg: '#edf8e5', fg: '#3f7410' },
  REJECTED: { bg: '#ffe9e9', fg: '#8f2323' },
};

const StatusChip: React.FC<{ status: DisplayState }> = ({ status }) => {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.DRAFT;
  return (
    <span
      style={{
        background: tone.bg,
        color: tone.fg,
        borderRadius: 4,
        padding: '3px 8px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
};

type Filter = 'ALL' | CourseStatus;

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'DRAFT', label: 'Borradores' },
  { value: 'PUBLISHED', label: 'Publicados' },
  { value: 'ARCHIVED', label: 'Archivados' },
];

/**
 * TCH-08 — the teacher's own course list (story S5.3).
 *
 * Previously the teacher navigation led only to the public catalog, which shows
 * every teacher's courses and offers to enrol you in them. This is the owned
 * view: status, lifecycle actions, and a route into authoring.
 */
const TeacherCourses: React.FC = () => {
  const navigate = useNavigate();
  const { state: appState, reason } = useTeacherApplication();

  const [courses, setCourses] = useState<TeacherCourseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('ALL');

  const load = useCallback(async () => {
    setError(null);
    try {
      const { courses: list } = await getMyCoursesWithEngagement();
      setCourses(list ?? []);
    } catch (err: any) {
      setError(err?.message || 'No pudimos cargar tus cursos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // What the server enforces is teacherProfile.verificationStatus; the
  // application state is the proxy this screen used while that field was not
  // exposed (S11.1). Prefer the authoritative one, fall back to the proxy only
  // when the profile could not be read — refusing to offer publishing because a
  // profile call timed out would block a verified teacher over a network blip,
  // and the server enforces the rule regardless.
  const { verified, loading: verificationLoading } = useTeacherVerification();
  const publishAllowed = verified ?? applicationApproved(appState);

  // The case that used to produce an unexplained 403: an approved application
  // whose activation never flipped the verification flag.
  const approvedButNotVerified = applicationApproved(appState) && verified === false;

  const runAction = async (
    courseId: string,
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    setBusyId(courseId);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(successMessage);
      await load();
    } catch (err: any) {
      setError(err?.message || 'No pudimos completar la acción.');
    } finally {
      setBusyId(null);
    }
  };

  const visible = useMemo(
    () => (filter === 'ALL' ? courses : courses.filter((c) => c.status === filter)),
    [courses, filter],
  );

  const counts = useMemo(() => {
    const by: Record<string, number> = { ALL: courses.length };
    for (const c of courses) by[c.status] = (by[c.status] ?? 0) + 1;
    return by;
  }, [courses]);

  if (loading) return <p>Cargando tus cursos...</p>;

  return (
    <PortalPage screenId="s_courses">
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <h1 style={{ margin: 0, flex: '1 1 auto' }}>Mis cursos</h1>
          <Link
            to="/teacher/courses/new"
            className="btn btn-primary"
            style={{ textDecoration: 'none' }}
          >
            Crear curso
          </Link>
        </div>
        <p style={{ color: '#666', marginTop: 0 }}>
          Los cursos que tú impartes. Para ver el catálogo público usa “Cursos”.
        </p>

        <ApplicationStatusBanner state={appState} reason={reason} />

        {approvedButNotVerified && (
          <div role="alert" style={alertStyle('#fff8e6', '#f5a623', '#7a4d06')}>
            Tu solicitud está aprobada, pero tu verificación todavía no está activa, así que aún no
            puedes publicar. Suele activarse en unos minutos; si sigue así, escríbenos.
          </div>
        )}

        {error && (
          <div role="alert" style={alertStyle('#ffe9e9', '#cb4242', '#8f2323')}>
            {error}
          </div>
        )}
        {notice && <div style={alertStyle('#edf8e5', '#5ba617', '#3f7410')}>{notice}</div>}

        {courses.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0' }}>
            {FILTERS.map((f) => {
              const active = filter === f.value;
              const count = counts[f.value] ?? 0;
              return (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  aria-pressed={active}
                  style={{
                    border: `1px solid ${active ? '#4a90e2' : '#d1d5da'}`,
                    background: active ? '#eaf2fb' : '#fff',
                    color: active ? '#1c4f80' : '#444',
                    fontWeight: active ? 700 : 500,
                    borderRadius: 999,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {f.label} ({count})
                </button>
              );
            })}
          </div>
        )}

        {courses.length === 0 ? (
          <EmptyState onCreate={() => navigate('/teacher/courses/new')} />
        ) : visible.length === 0 ? (
          <p style={{ color: '#666' }}>No tienes cursos en este estado.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visible.map((course) => {
              const busy = busyId === course.listingId;
              const state = displayStateOf(course);
              // The same server-enforced verification gates sending to review
              // and publishing an approved course (TCH-06-FR-34).
              const gateTitle = publishAllowed
                ? undefined
                : approvedButNotVerified
                ? 'Tu solicitud está aprobada pero tu verificación aún no está activa. Escríbenos si no se activa pronto.'
                : 'Podrás enviar cursos a revisión cuando tu solicitud de profesor sea aprobada.';
              const gated = busy || verificationLoading || !publishAllowed;
              return (
                <div
                  key={course.listingId}
                  style={{
                    border: '1px solid #d1d5da',
                    borderRadius: 8,
                    padding: 16,
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                    }}
                  >
                    <strong style={{ fontSize: 16 }}>{course.title}</strong>
                    <StatusChip status={state} />
                  </div>

                  <ReviewNote course={course} state={state} />

                  <div style={{ color: '#666', fontSize: 14, margin: '8px 0 12px' }}>
                    {course.category} · {course.duration} min ·{' '}
                    {course.offerMode === 'ONE_ON_ONE'
                      ? 'Individual'
                      : `hasta ${course.maxStudents} alumnos`}{' '}
                    · {formatMoney(course.price, course.currency)}
                    {course.gradeLevel?.length > 0 && <> · {course.gradeLevel.join(', ')}</>}
                  </div>

                  {/*
                    What the course is actually doing. The list used to show only
                    what it was authored as, so a teacher could not tell a course
                    with eight children from one nobody had ever taken
                    (TCH-08-FR-02 / FR-05).
                  */}
                  <div style={{ color: '#444', fontSize: 13, marginBottom: 12 }}>
                    {course.learners === 0
                      ? 'Sin alumnos todavía'
                      : `${course.learners} ${course.learners === 1 ? 'alumno' : 'alumnos'}`}
                    {course.activeEnrollments > 0 && <> · {course.activeEnrollments} en curso</>}
                    {course.updatedAt && <> · actualizado {formatDay(course.updatedAt)}</>}
                  </div>

                  {course.scheduleType === 'RECURRING' && course.schedule && (
                    <div style={{ color: '#1c4f80', fontSize: 13, marginBottom: 12 }}>
                      🗓 {describeSchedule(course.schedule)}
                      {course.durationMode === 'FIXED' && course.durationWeeks
                        ? ` · ${course.durationWeeks} semanas`
                        : ''}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* S5.5 — the roster, progress and next meeting for this course. */}
                    <Link
                      to={`/teacher/courses/${course.listingId}`}
                      className="btn btn-outline btn-sm"
                      style={{ textDecoration: 'none' }}
                    >
                      Ver alumnos
                    </Link>
                    {/*
                      UC-A08: a draft is sent to review, not published. The
                      reviewer approves the exact version sent, and only then
                      does it go live (TCH-06-FR-33 changed meaning, 12 Oct 2026).
                    */}
                    {(state === 'DRAFT' || state === 'CHANGES_REQUIRED') && (
                      <>
                        <Link
                          to={`/teacher/courses/${course.listingId}/edit`}
                          className="btn btn-outline btn-sm"
                          style={{ textDecoration: 'none' }}
                        >
                          Editar
                        </Link>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={gated}
                          title={gateTitle}
                          onClick={() =>
                            runAction(
                              course.listingId,
                              () => submitCourseForReview(course.listingId),
                              `“${course.title}” se envió a revisión. Lo verás aquí como “En revisión” hasta que el equipo responda.`,
                            )
                          }
                        >
                          {busy
                            ? 'Enviando…'
                            : state === 'CHANGES_REQUIRED'
                            ? 'Reenviar a revisión'
                            : 'Enviar a revisión'}
                        </button>
                      </>
                    )}

                    {/* Approved, but another gate held publication back (AD-A08-05). */}
                    {state === 'APPROVED' && (
                      <>
                        <Link
                          to={`/teacher/courses/${course.listingId}/edit`}
                          className="btn btn-outline btn-sm"
                          style={{ textDecoration: 'none' }}
                        >
                          Editar
                        </Link>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={gated}
                          title={gateTitle}
                          onClick={() =>
                            runAction(
                              course.listingId,
                              () => publishCourse(course.listingId),
                              `“${course.title}” ya está publicado.`,
                            )
                          }
                        >
                          {busy ? 'Publicando…' : 'Publicar'}
                        </button>
                      </>
                    )}

                    {course.status === 'PUBLISHED' && (
                      <>
                        <Link
                          to={`/courses/${course.listingId}`}
                          className="btn btn-outline btn-sm"
                          style={{ textDecoration: 'none' }}
                        >
                          Ver como lo ven los padres
                        </Link>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={busy}
                          onClick={() =>
                            runAction(
                              course.listingId,
                              () => unpublishCourse(course.listingId),
                              `“${course.title}” volvió a borrador. Ya puedes editarlo.`,
                            )
                          }
                        >
                          {busy ? 'Guardando…' : 'Despublicar'}
                        </button>
                      </>
                    )}

                    {course.status !== 'ARCHIVED' && (
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => {
                          const ok = window.confirm(
                            `¿Archivar “${course.title}”? Dejará de aparecer en el catálogo. Las inscripciones en curso no se cancelan.`,
                          );
                          if (!ok) return;
                          runAction(
                            course.listingId,
                            () => archiveCourse(course.listingId),
                            `“${course.title}” fue archivado.`,
                          );
                        }}
                      >
                        Archivar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PortalPage>
  );
};

/**
 * What the reviewer said, and what the teacher can do about it.
 *
 * The feedback is shown verbatim: it is written for the teacher (AC-07,
 * "actionable and tied to version"), and paraphrasing it would lose the part
 * they need.
 */
const ReviewNote: React.FC<{ course: TeacherCourseSummary; state: DisplayState }> = ({
  course,
  state,
}) => {
  if (state === 'PENDING_REVIEW') {
    return (
      <p style={{ color: '#1c4f80', fontSize: 13, margin: '8px 0 0' }}>
        Enviado a revisión
        {course.submittedAt ? <> el {formatDay(course.submittedAt)}</> : null}. No puedes editarlo
        mientras el equipo lo revisa.
      </p>
    );
  }
  if (state === 'CHANGES_REQUIRED' || state === 'REJECTED') {
    const used = course.resubmissionCount ?? 0;
    return (
      <div
        style={alertStyle(
          state === 'REJECTED' ? '#ffe9e9' : '#fff8e6',
          state === 'REJECTED' ? '#cb4242' : '#f5a623',
          state === 'REJECTED' ? '#8f2323' : '#7a4d06',
        )}
      >
        <strong>
          {state === 'REJECTED' ? 'Motivo del rechazo:' : 'El equipo de revisión pide cambios:'}
        </strong>{' '}
        {course.reviewFeedback ?? 'Sin comentario.'}
        {state === 'CHANGES_REQUIRED' ? (
          <div style={{ marginTop: 6 }}>
            Edita el curso y reenvíalo.
            {used > 0 ? ` Reenvíos usados: ${used}.` : ''}
          </div>
        ) : (
          <div style={{ marginTop: 6 }}>
            Este curso ya no puede reenviarse. Si quieres ofrecerlo, crea un curso nuevo.
          </div>
        )}
      </div>
    );
  }
  if (state === 'APPROVED') {
    return (
      <p style={{ color: '#3f7410', fontSize: 13, margin: '8px 0 0' }}>
        El equipo aprobó este curso, pero todavía no está publicado. Publícalo cuando tu
        verificación esté activa.
      </p>
    );
  }
  return null;
};

const EmptyState: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <div
    style={{
      border: '1px dashed #d1d5da',
      borderRadius: 8,
      padding: 32,
      textAlign: 'center',
      color: '#666',
    }}
  >
    <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#333' }}>Todavía no tienes cursos</p>
    <p style={{ margin: '0 0 16px', fontSize: 14 }}>
      Crea tu primer curso como borrador. Cuando tu solicitud sea aprobada podrás enviarlo a
      revisión; se publica cuando el equipo lo aprueba.
    </p>
    <button className="btn btn-primary" onClick={onCreate}>
      Crear mi primer curso
    </button>
  </div>
);

function alertStyle(bg: string, border: string, fg: string): React.CSSProperties {
  return {
    background: bg,
    border: `1px solid ${border}`,
    color: fg,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 12,
    fontSize: 14,
  };
}

export default TeacherCourses;
